import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// One container on <body> shared by every open modal. It is a full-screen
// fixed layer that takes the clicks, so it must go with the last modal: left
// behind, the whole page stops responding (19/10, fermeture du pop-up
// « Nouveautés »).
//
// Users are counted in an effect, never during render: React may render a
// modal and throw that render away (Suspense of a lazy modal, StrictMode,
// interrupted render), and a count taken in render then never came back down.
let sharedContainer = null;
// Mounted modals per container (normally just the shared one).
const users = new Map();

const createContainer = () => {
  const div = document.createElement('div');
  div.style.position = 'fixed';
  div.style.top = '0';
  div.style.left = '0';
  div.style.width = '100%';
  div.style.height = '100%';
  div.style.pointerEvents = 'auto';
  return div;
};

const removeIfUnused = (container) => {
  if (users.get(container) > 0) return;
  users.delete(container);
  container.remove();
  if (sharedContainer === container) sharedContainer = null;
};

const getContainer = () => {
  if (!sharedContainer) {
    const div = createContainer();
    sharedContainer = div;
    // Attached now so the modal's content is measured in the page on its first
    // commit; dropped again if that render never commits.
    document.body.appendChild(div);
    setTimeout(() => removeIfUnused(div), 0);
  }
  return sharedContainer;
};

const ModalPortal = ({ children, zIndex = 50 }) => {
  const [container] = useState(() => (typeof document === 'undefined' ? null : getContainer()));

  useLayoutEffect(() => {
    if (!container) return undefined;
    users.set(container, (users.get(container) || 0) + 1);
    // Re-attached if it was dropped between render and commit.
    if (!container.isConnected) document.body.appendChild(container);
    if (!sharedContainer) sharedContainer = container;
    return () => {
      users.set(container, users.get(container) - 1);
      removeIfUnused(container);
    };
  }, [container]);

  useLayoutEffect(() => {
    if (container) container.style.zIndex = String(zIndex);
  }, [zIndex, container]);

  if (!container) return null;
  return createPortal(children, container);
};

export default ModalPortal;
